import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { Spinner } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import {
    Search, RefreshCw, ChevronLeft, ChevronRight,
    Film, Lock, AlertTriangle, SearchX, SlidersHorizontal,
} from "lucide-react"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

import SEO from "../../components/Common/SEO"
import KeywordSuggestionsDetailsModal from "../../components/Common/Modal/KeywordSuggestionsDetailsModal"
import RecentApps from "../../components/Common/RecentApps"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl } from "../../utils/seoUtils"
import { api } from "../../utils/variables"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

import useUgcVideos, { UGC_STATUS } from "./hooks/useUgcVideos"
import useSentimentUgc from "./hooks/useSentimentUgc"
import { fetchUgcVideos, forceRefreshUgcVideos, fetchUgcRecentApps } from "./ugcExplorerApi"
import { DEFAULT_PLATFORM, UGC_PLATFORMS, getPlatformMeta, getSortOptions, getDefaultFilters, getPageSize } from "./constants"
import { formatUtcDate, countActiveFilters } from "./utils"
import VideoCard from "./components/VideoCard"
import ReelCard from "./components/ReelCard"
import TikTokCard from "./components/TikTokCard"
import VideoDetailSection from "./components/VideoDetailSection"
import PlatformTabs from "./components/PlatformTabs"
import SortDropdown from "./components/SortDropdown"
import FiltersPanel from "./components/FiltersPanel"
import SentimentCta from "./components/sentiment/SentimentCta"
import SentimentPanel from "./components/sentiment/SentimentPanel"

const UGCExplorer = () => {
    const navigate = useNavigate()
    const { platform: platformParam, appId: appIdParam } = useParams()
    const { trackEvent } = useAnalytics()

    const [searchTerm, setSearchTerm] = useState("")
    const [showSearchModal, setShowSearchModal] = useState(false)
    const [selectedApp, setSelectedApp] = useState(null)
    const [platform, setPlatform] = useState(DEFAULT_PLATFORM)
    const [deepLinkLoading, setDeepLinkLoading] = useState(!!appIdParam)
    const [page, setPage] = useState(1)
    const [sort, setSort] = useState("views")
    const [activeVideo, setActiveVideo] = useState(null)
    const [refreshing, setRefreshing] = useState(false)
    const [filters, setFilters] = useState(getDefaultFilters(DEFAULT_PLATFORM))
    const [showFilters, setShowFilters] = useState(false)
    const [filterOptions, setFilterOptions] = useState(null)
    const [recentApps, setRecentApps] = useState([])
    const [loadingRecentApps, setLoadingRecentApps] = useState(true)
    // Which pagination button was last clicked ("prev" | "next" | null) - drives
    // which button shows its own spinner while that page is being fetched.
    const [navigating, setNavigating] = useState(null)
    // Comment sentiment (SENTIMENT_UGC_API.md) covers every platform at once,
    // so it swaps in for the whole video grid rather than living on a tab.
    const [showSentiment, setShowSentiment] = useState(false)
    // Platforms still to try when the one on screen turns out to have no
    // coverage for a freshly opened app (YouTube -> Instagram -> TikTok, then
    // back to YouTube's "No coverage found" if none have any). `null` once
    // settled, or as soon as the user picks a tab themselves.
    const [autoPickQueue, setAutoPickQueue] = useState(null)
    // Anchors the toolbar just above the grid - Prev/Next live at the bottom
    // of the page, so a click needs to scroll the new page back into view
    // rather than leaving the user staring at whatever was at the bottom of
    // the old one.
    const resultsTopRef = useRef(null)

    const appId = selectedApp ? String(selectedApp.item_id || selectedApp._id) : null
    const platformMeta = getPlatformMeta(platform)

    const pageSize = getPageSize(platform)
    const { data, status, error, isFetching, reload } = useUgcVideos({
        appId, platform, page, pageSize, sort, filters,
    })
    // Only requests anything on an explicit "Generate" (or for an app this
    // browser already generated) - the first call claims the app against the
    // user's sentiment app limit.
    const sentiment = useSentimentUgc(appId)

    // A different app never opens straight onto the previous app's sentiment.
    useEffect(() => { setShowSentiment(false) }, [appId])

    const enabledPlatformIds = UGC_PLATFORMS.filter(p => p.enabled).map(p => p.id)
    const firstPlatform = enabledPlatformIds[0] || DEFAULT_PLATFORM

    // Every fresh app open starts on the first platform with the rest queued
    // up as fallbacks. Set in the same batch as the app itself so the first
    // request already goes to the right platform.
    const startAutoPlatformPick = () => {
        setPlatform(firstPlatform)
        setAutoPickQueue(enabledPlatformIds.slice(1))
    }

    // Walks the fallback queue: only once *this* app+platform's own response
    // has landed (the `_appId`/`_platform` tags - right after a switch `data`
    // still holds the previous platform's page for a render) and it has no
    // coverage at all, move on to the next platform. If none has any, settle
    // back on the first so its "No coverage found" state is what shows.
    useEffect(() => {
        if (autoPickQueue === null || !appId) return
        if ([UGC_STATUS.FAILED, UGC_STATUS.ERROR, UGC_STATUS.FORBIDDEN, UGC_STATUS.NOT_FOUND].includes(status)) {
            setAutoPickQueue(null)
            return
        }
        if (status !== UGC_STATUS.READY || data?._appId !== appId || data?._platform !== platform) return

        const hasCoverage = (data.items?.length ?? 0) > 0 || (data.total_items ?? 0) > 0 || !!data.filtered
        if (hasCoverage) {
            setAutoPickQueue(null)
            return
        }
        if (autoPickQueue.length > 0) {
            setPlatform(autoPickQueue[0])
            setPage(1)
            setAutoPickQueue(autoPickQueue.slice(1))
            return
        }
        setAutoPickQueue(null)
        if (platform !== firstPlatform) {
            setPlatform(firstPlatform)
            setPage(1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, data, platform, appId, autoPickQueue])

    // While the queue is still being walked, an empty platform is an
    // in-between step rather than the answer - keep showing the loading state
    // instead of flashing "No coverage found" for each platform on the way.
    const isAutoPicking = autoPickQueue !== null && !(status === UGC_STATUS.READY && (data?.items?.length ?? 0) > 0)

    // "Apps you last explored UGC for" (RECENT_APPS_API.md) - same read-only,
    // no-cost list AI CMO shows as "Recently Opened". YouTube and Instagram
    // keep independent lists (different `recentAppsFeature` per platform in
    // constants.js), so this reloads whenever the active tab changes.
    const loadRecentApps = async (platformId) => {
        setLoadingRecentApps(true)
        try {
            const result = await fetchUgcRecentApps({ limit: 10, platform: platformId })
            setRecentApps(result?.recent_apps || [])
        } catch (err) {
            console.error("Error fetching UGC recent apps:", err)
        } finally {
            setLoadingRecentApps(false)
        }
    }

    useEffect(() => { loadRecentApps(platform) }, [platform]) // eslint-disable-line react-hooks/exhaustive-deps

    // Deep link from a website notification, e.g.
    // "/ugc-explorer/youtube/6061406827268889" (RECENT_VIDEO_FILTERS notification
    // support) - resolve the app id to a real app (name/image_url) via the same
    // endpoint gameDashboard.jsx uses, then auto-select it and its platform so
    // the videos load exactly as if the user had searched and picked it.
    useEffect(() => {
        if (!appIdParam) return
        let cancelled = false
        const loadDeepLinkedApp = async () => {
            setDeepLinkLoading(true)
            try {
                const token = localStorage.getItem("authToken")
                const headers = token ? { Authorization: `Bearer ${token}` } : {}
                const res = await axios.get(`${api}/tracker/app/${appIdParam}`, { headers })
                const details = res?.data?.details
                if (cancelled) return
                if (details && Object.keys(details).length > 0) {
                    setSelectedApp({
                        item_id: details.item_id || appIdParam,
                        name: details.name,
                        image_url: details.image_url,
                    })
                    // A link that names a platform opens exactly that one;
                    // otherwise it falls back across platforms like a search.
                    const resolvedPlatform = UGC_PLATFORMS.find(p => p.id === platformParam && p.enabled)?.id
                    if (resolvedPlatform) {
                        setPlatform(resolvedPlatform)
                        setAutoPickQueue(null)
                    } else {
                        startAutoPlatformPick()
                    }
                    setPage(1)
                    setActiveVideo(null)
                } else {
                    toastr.error("We couldn't find that app.")
                }
            } catch (err) {
                if (!cancelled) toastr.error(err?.response?.data?.error || "Couldn't load that app right now.")
            } finally {
                if (!cancelled) setDeepLinkLoading(false)
            }
        }
        loadDeepLinkedApp()
        return () => { cancelled = true }
        // Only ever run for the id/platform this page was loaded with.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appIdParam, platformParam])

    // The list only moves once the feature call it's tied to has actually
    // landed (API doc §4.2) - refetch once this app's coverage settles so a
    // freshly-searched app jumps to the front without a page reload.
    useEffect(() => {
        if (status === UGC_STATUS.READY) loadRecentApps(platform)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status])

    // Facets describe the app's whole stored set, not the filtered page, so
    // they're fetched once per app/platform rather than alongside every page
    // of results (RECENT_VIDEO_FILTERS.md §5). Switching platform also resets
    // the sort and filter shape, since each platform has its own sort/filter
    // vocabulary (constants.js's getSortOptions/getDefaultFilters).
    useEffect(() => {
        setFilters(getDefaultFilters(platform))
        setSort("views")
        setFilterOptions(null)
        setShowFilters(false)
        // Belt-and-braces alongside handleSelectApp/handleSelectRecentApp - any
        // path that swaps the app should never leave a stale video detail view
        // showing over the new app's (still loading) results.
        setActiveVideo(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, platform])

    useEffect(() => {
        if (!appId || status !== UGC_STATUS.READY) return
        let cancelled = false
        fetchUgcVideos({ appId, platform, page: 1, pageSize: 1, includeFacets: true })
            .then(result => { if (!cancelled) setFilterOptions(result?.filter_options || null) })
            .catch(() => {})
        return () => { cancelled = true }
    }, [appId, platform, status])

    // Instagram reels and TikTok videos are both vertical (9:16) source video,
    // so both get the narrower "reels" grid columns and taller skeletons -
    // only YouTube's 16:9 thumbnails want the wider default layout.
    const isPortraitPlatform = platform === "instagram" || platform === "tiktok"
    const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters])

    // Clears whichever Prev/Next spinner was showing once its fetch actually
    // lands - covers success, failure and being superseded by a newer click
    // alike, since all of those end with `isFetching` going false.
    useEffect(() => {
        if (!isFetching) setNavigating(null)
    }, [isFetching])

    const scrollToResultsTop = () => {
        resultsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    const handleSelectApp = (app) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_APP_SELECTED, {
            app_id: app.item_id || app._id,
            app_name: app.name,
        })
        setSelectedApp(app)
        startAutoPlatformPick()
        setPage(1)
        setActiveVideo(null)
        setShowSearchModal(false)
    }

    const handleSelectRecentApp = (recentApp) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_APP_SELECTED, {
            app_id: recentApp.app_id,
            app_name: recentApp.name,
        })
        setSelectedApp({ item_id: recentApp.app_id, name: recentApp.name, image_url: recentApp.image_url })
        startAutoPlatformPick()
        setPage(1)
        setActiveVideo(null)
        setSearchTerm("")
    }

    const handleFiltersChange = (nextFilters) => {
        const changedKey = Object.keys(nextFilters).find(
            key => JSON.stringify(nextFilters[key]) !== JSON.stringify(filters[key])
        )
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_FILTER_APPLIED, {
            app_id: appId,
            platform,
            filter_key: changedKey || null,
            filter_value: changedKey ? JSON.stringify(nextFilters[changedKey]) : null,
        })
        setFilters(nextFilters)
        setPage(1)
    }

    const handleClearFilters = () => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_FILTER_CLEARED, { app_id: appId, platform })
        setFilters(getDefaultFilters(platform))
        setPage(1)
    }

    const handleBackToSearch = () => {
        setSelectedApp(null)
        setAutoPickQueue(null)
        setPage(1)
        setActiveVideo(null)
    }

    const handleSearch = () => {
        if (!searchTerm.trim()) return
        setShowSearchModal(true)
    }

    const handleSortChange = (value) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_SORT_CHANGED, { app_id: appId, platform, sort: value })
        setSort(value)
        setPage(1)
    }

    const handlePlatformChange = (nextPlatform) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_PLATFORM_CHANGED, { platform: nextPlatform })
        // A tab the user picked themselves is never auto-switched away from.
        setAutoPickQueue(null)
        setPlatform(nextPlatform)
        setPage(1)
    }

    const handleForceRefresh = async () => {
        if (!appId || refreshing) return
        setRefreshing(true)
        try {
            await forceRefreshUgcVideos({ appId, platform })
            trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_REFRESHED, { app_id: appId, platform })
            toastr.success("Refresh started - updated coverage will appear shortly.")
            reload()
        } catch (err) {
            toastr.error(err.message || "Couldn't start a refresh right now.")
        } finally {
            setRefreshing(false)
        }
    }

    const handleGenerateSentiment = () => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_SENTIMENT_GENERATED, { app_id: appId, app_name: selectedApp?.name })
        setActiveVideo(null)
        setShowSentiment(true)
        sentiment.generate()
    }

    const handleOpenSentiment = () => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_SENTIMENT_VIEWED, { app_id: appId, status: sentiment.status })
        setActiveVideo(null)
        setShowSentiment(true)
    }

    const handleOpenVideo = (video) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_VIDEO_OPENED, {
            video_id: video.video_id,
            platform: video.platform,
            app_id: appId,
        })
        setActiveVideo(video)
    }

    const items = data?.items || []
    const upgradeInfo = status === UGC_STATUS.FORBIDDEN ? (error || {}) : null

    return (
        <React.Fragment>
            <SEO
                title={PAGE_SEO.ugcExplorer.title}
                description={PAGE_SEO.ugcExplorer.description}
                keywords={PAGE_SEO.ugcExplorer.keywords}
                canonical={generateCanonicalUrl('/ugc-explorer')}
                robots={PAGE_SEO.ugcExplorer.robots}
            />

            <div className="page-content">
                <div className="container-fluid" style={{ fontFamily: "Rubik, sans-serif" }}>

                    {/* ── Header ─────────────────────────────────────────── */}
                    <div className="mb-4 mt-2">
                        <h1 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: "28px" }}>
                            UGC Explorer
                        </h1>
                        <p style={{ fontSize: "15px", maxWidth: "900px", color: "#9ca3af", fontWeight: 400, marginTop: "8px", marginBottom: "0" }}>
                            Every verified piece of user-generated video coverage for any VR or XR app - reviews, gameplay,
                            tutorials and more - AI-summarized, sentiment-scored, and ranked by views.
                        </p>
                    </div>

                    {/* ── Find an App ────────────────────────────────────── */}
                    <div className="ugc-find-app-card" style={{
                        background: "#21222d",
                        border: "1px solid rgba(255,255,255,0.05)",
                        borderRadius: "20px",
                        marginBottom: "24px",
                    }}>
                        <div className="mb-4">
                            <h2 className="text-white mb-2" style={{ fontWeight: 600, fontSize: '21.6px' }}>Find an App</h2>
                            <p style={{ fontSize: "14px", color: "#e8e8e8" }}>Search for any app to explore its verified UGC video coverage.</p>
                        </div>
                        <div className="ugc-search-row">
                            <div className="ugc-search-box d-flex align-items-center" style={{
                                maxWidth: "700px",
                                background: "#21222d",
                                border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: "12px",
                            }}>
                                <Search size={16} className="ms-3" style={{ color: "#adb5bd", flexShrink: 0 }} />
                                <input
                                    type="text"
                                    placeholder="Enter App or Game name"
                                    className="flex-grow-1"
                                    style={{ background: "transparent", border: "none", color: "#fff", padding: "12px", fontFamily: "Rubik", minWidth: 0, width: "100%" }}
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                                />
                            </div>
                            <div className="ugc-search-actions">
                                <button onClick={handleSearch} className="ugc-btn-primary">
                                    <Search size={16} /> Search
                                </button>
                            </div>
                        </div>
                    </div>

                    {!selectedApp && !deepLinkLoading && (
                        <RecentApps apps={recentApps} loading={loadingRecentApps} onSelect={handleSelectRecentApp} />
                    )}

                    {/* ── Main content ───────────────────────────────────── */}
                    {deepLinkLoading ? (
                        <div className="text-center py-5" style={{ background: "#21222d", borderRadius: "24px", border: "1px dashed rgba(255,255,255,0.1)" }}>
                            <Spinner size="sm" style={{ color: "#F6255A" }} />
                            <p className="mt-3 mb-0" style={{ color: "#e8e8e8", fontSize: "13.5px" }}>Loading app from your notification…</p>
                        </div>
                    ) : !selectedApp ? (
                        (loadingRecentApps || recentApps.length === 0) && (
                        <div className="text-center py-5" style={{ background: "#21222d", borderRadius: "24px", border: "1px dashed rgba(255,255,255,0.1)" }}>
                            <div style={{ maxWidth: "500px", margin: "0 auto" }}>
                                <div style={{
                                    width: "80px", height: "80px", background: "rgba(246,37,90,0.05)", borderRadius: "50%",
                                    display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px",
                                    border: "1px solid rgba(246,37,90,0.2)",
                                }}>
                                    <Film size={32} color="#F6255A" />
                                </div>
                                <h2 className="text-white mb-3" style={{ fontSize: '21.6px' }}>Ready to Explore</h2>
                                <p style={{ lineHeight: "1.6", color: "#e8e8e8" }} className="mb-4">
                                    Search for an app above to see every verified {platformMeta.label} {platformMeta.itemNoun} that talks
                                    about it, complete with AI summaries, sentiment and content-type tags.
                                </p>
                                <button onClick={() => setShowSearchModal(true)} className="ugc-btn-outline">
                                    Start Exploring
                                </button>
                            </div>
                        </div>
                        )
                    ) : (
                        <div style={{ background: "#21222d", borderRadius: "16px", padding: "24px" }}>
                          {activeVideo ? (
                            <VideoDetailSection video={activeVideo} onBack={() => setActiveVideo(null)} />
                          ) : showSentiment ? (
                            <SentimentPanel
                                app={selectedApp}
                                sentiment={sentiment}
                                onBack={() => setShowSentiment(false)}
                                onGenerate={sentiment.generate}
                            />
                          ) : (
                            <>
                            {/* Leaves this app's coverage and returns to the
                                main screen (Find an App) without clearing
                                the search box. */}
                            <button
                                type="button"
                                onClick={handleBackToSearch}
                                className="ugc-btn-ghost mb-3"
                            >
                                <ChevronLeft size={14} /> Back to UGC Explorer
                            </button>

                            {/* App header */}
                            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <img
                                        src={selectedApp.image_url}
                                        alt={selectedApp.name}
                                        style={{ width: "56px", height: "56px", borderRadius: "12px", objectFit: "cover" }}
                                    />
                                    <div>
                                        <h2 className="text-white mb-1" style={{ fontWeight: 600, fontSize: "20px" }}>{selectedApp.name}</h2>
                                        <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                                            Verified {platformMeta.label} coverage
                                        </span>
                                    </div>
                                </div>

                                <PlatformTabs active={platform} onChange={handlePlatformChange} />
                            </div>

                            {status !== UGC_STATUS.NOT_FOUND && (
                                <SentimentCta
                                    appName={selectedApp.name}
                                    sentiment={sentiment}
                                    onGenerate={handleGenerateSentiment}
                                    onOpen={handleOpenSentiment}
                                />
                            )}

                            {/* Toolbar: counts / freshness / sort / refresh */}
                            {status === UGC_STATUS.READY && !isAutoPicking && (
                                <div ref={resultsTopRef} className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4"
                                    style={{ paddingBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                    <div style={{ fontSize: "13px", color: "#9ca3af" }}>
                                        <strong style={{ color: "#fff" }}>{data?.total_items ?? 0}</strong> {(data?.total_items ?? 0) === 1 ? platformMeta.itemNoun : platformMeta.itemNounPlural}
                                        {data?.filtered && typeof data?.total_items_unfiltered === "number" && (
                                            <span> of {data.total_items_unfiltered}</span>
                                        )} found
                                        {data?.last_completed_at && (
                                            <span> · Last checked {formatUtcDate(data.last_completed_at)}</span>
                                        )}
                                        {data?.refresh_due && <span> · Refresh due</span>}
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => setShowFilters(v => !v)}
                                            className="ugc-btn-ghost"
                                            style={activeFilterCount > 0 ? { color: "#F6255A", borderColor: "rgba(246,37,90,0.4)" } : undefined}
                                        >
                                            <SlidersHorizontal size={14} /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                                        </button>
                                        <SortDropdown value={sort} options={getSortOptions(platform)} onChange={handleSortChange} />
                                        <button
                                            onClick={handleForceRefresh}
                                            disabled={refreshing || !data?.refresh_due}
                                            title={data?.refresh_due ? "Refresh this app's coverage now" : `Next automatic refresh ${formatUtcDate(data?.next_refresh_at) || "later"}`}
                                            className="ugc-btn-ghost"
                                        >
                                            {refreshing ? <Spinner size="sm" /> : <RefreshCw size={14} />} Refresh
                                        </button>
                                    </div>
                                </div>
                            )}

                            {status === UGC_STATUS.READY && showFilters && (
                                <FiltersPanel
                                    platform={platform}
                                    filters={filters}
                                    onChange={handleFiltersChange}
                                    facets={filterOptions}
                                    onClear={handleClearFilters}
                                    activeCount={activeFilterCount}
                                    onClose={() => setShowFilters(false)}
                                />
                            )}

                            {/* Content states */}
                            {(status === UGC_STATUS.LOADING || status === UGC_STATUS.PROCESSING || (isAutoPicking && status === UGC_STATUS.READY)) && (
                                <>
                                    {status === UGC_STATUS.PROCESSING && (
                                        <div style={{
                                            display: "flex", alignItems: "center", gap: "12px",
                                            background: "rgba(246,37,90,0.06)", border: "1px solid rgba(246,37,90,0.2)",
                                            borderRadius: "12px", padding: "14px 18px", marginBottom: "20px",
                                        }}>
                                            <Spinner size="sm" style={{ color: "#F6255A" }} />
                                            <span style={{ color: "#e8e8e8", fontSize: "13.5px" }}>
                                                Finding verified {platformMeta.label} coverage for <strong>{selectedApp.name}</strong> - this can take up to 3 minutes.
                                                We'll keep checking automatically.
                                            </span>
                                        </div>
                                    )}
                                    {isAutoPicking && status !== UGC_STATUS.PROCESSING && platform !== firstPlatform && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#9ca3af", fontSize: "13px", marginBottom: "16px" }}>
                                            <Spinner size="sm" style={{ width: 14, height: 14, color: "#F6255A" }} />
                                            No coverage on the previous platform - checking {platformMeta.label}…
                                        </div>
                                    )}
                                    <div className={`ugc-video-grid${isPortraitPlatform ? " ugc-video-grid--reels" : ""}`}>
                                        {Array.from({ length: pageSize }).map((_, i) => (
                                            <div key={i} style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "16px", overflow: "hidden" }}>
                                                <Skeleton height={isPortraitPlatform ? 320 : 160} baseColor="#2a2a35" highlightColor="#33333f" style={{ display: "block" }} />
                                                <div style={{ padding: "14px 16px" }}>
                                                    <Skeleton width="90%" baseColor="#2a2a35" highlightColor="#33333f" />
                                                    <Skeleton width="60%" baseColor="#2a2a35" highlightColor="#33333f" />
                                                    <Skeleton width="100%" height={32} style={{ marginTop: 8 }} baseColor="#2a2a35" highlightColor="#33333f" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {status === UGC_STATUS.READY && items.length === 0 && !isAutoPicking && (
                                <div className="text-center" style={{ background: "rgba(30,31,41,0.4)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)", padding: "56px 20px" }}>
                                    <div style={{ width: "56px", height: "56px", background: "rgba(246,37,90,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                        <SearchX size={24} color="#F6255A" />
                                    </div>
                                    {data?.filtered ? (
                                        <>
                                            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>No {platformMeta.itemNounPlural} match these filters</h6>
                                            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", maxWidth: "380px", margin: "0 auto 16px", lineHeight: "1.5" }}>
                                                {selectedApp.name} has {data?.total_items_unfiltered ?? 0} {(data?.total_items_unfiltered ?? 0) === 1 ? platformMeta.itemNoun : platformMeta.itemNounPlural} in total - try loosening a filter.
                                            </p>
                                            <button onClick={handleClearFilters} className="ugc-btn-outline">Clear Filters</button>
                                        </>
                                    ) : (
                                        <>
                                            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>No coverage found yet</h6>
                                            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", maxWidth: "380px", margin: "0 auto", lineHeight: "1.5" }}>
                                                {data?.message || `We couldn't find any verified ${platformMeta.label} ${platformMeta.itemNounPlural} about ${selectedApp.name} yet.`}
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}

                            {status === UGC_STATUS.READY && items.length > 0 && (
                                <>
                                    {/* Page/sort/filter changes keep showing this same
                                        grid while they refetch (see useUgcVideos'
                                        `isFetching`) rather than blanking to a
                                        skeleton - dimming it and blocking clicks is
                                        the lighter-weight "this is stale, hang on"
                                        signal for that window, on top of whichever
                                        Prev/Next button is spinning below. */}
                                    <div
                                        className={`ugc-video-grid${isPortraitPlatform ? " ugc-video-grid--reels" : ""}`}
                                        style={{
                                            opacity: isFetching ? 0.45 : 1,
                                            pointerEvents: isFetching ? "none" : "auto",
                                            transition: "opacity 0.15s ease",
                                        }}
                                    >
                                        {items.map(item => (
                                            item.platform === "instagram"
                                                ? <ReelCard key={item.video_id} video={item} onOpen={handleOpenVideo} />
                                                : item.platform === "tiktok"
                                                    ? <TikTokCard key={item.video_id} video={item} onOpen={handleOpenVideo} />
                                                    : <VideoCard key={item.video_id} video={item} onOpen={handleOpenVideo} />
                                        ))}
                                    </div>

                                    {(data?.has_previous || data?.has_next) && (
                                        <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                                            <button
                                                onClick={() => {
                                                    setNavigating("prev")
                                                    setPage(p => {
                                                        const nextPage = Math.max(1, p - 1)
                                                        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_PAGINATED, { app_id: appId, platform, page: nextPage })
                                                        return nextPage
                                                    })
                                                    scrollToResultsTop()
                                                }}
                                                disabled={!data?.has_previous || isFetching}
                                                className="ugc-btn-ghost"
                                            >
                                                {navigating === "prev" && isFetching ? <Spinner size="sm" /> : <ChevronLeft size={15} />} Prev
                                            </button>
                                            <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                                                Page {data?.page ?? page} of {data?.total_pages ?? 1}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    setNavigating("next")
                                                    setPage(p => {
                                                        const nextPage = p + 1
                                                        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_EXPLORER_PAGINATED, { app_id: appId, platform, page: nextPage })
                                                        return nextPage
                                                    })
                                                    scrollToResultsTop()
                                                }}
                                                disabled={!data?.has_next || isFetching}
                                                className="ugc-btn-ghost"
                                            >
                                                Next {navigating === "next" && isFetching ? <Spinner size="sm" /> : <ChevronRight size={15} />}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {(status === UGC_STATUS.FAILED || status === UGC_STATUS.ERROR) && (
                                <div className="text-center" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "16px", padding: "40px 20px" }}>
                                    <AlertTriangle size={28} color="#ef4444" style={{ marginBottom: "12px" }} />
                                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px" }}>Something went wrong</h6>
                                    <p style={{ color: "#9ca3af", fontSize: "13px", maxWidth: "420px", margin: "0 auto 16px" }}>
                                        {typeof error === "string" ? error : "We couldn't load UGC coverage for this app."}
                                    </p>
                                    <button onClick={reload} className="ugc-btn-outline">Try Again</button>
                                </div>
                            )}

                            {status === UGC_STATUS.NOT_FOUND && (
                                <div className="text-center" style={{ background: "rgba(30,31,41,0.4)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)", padding: "40px 20px" }}>
                                    <SearchX size={28} color="#9ca3af" style={{ marginBottom: "12px" }} />
                                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px" }}>App not found</h6>
                                    <p style={{ color: "#9ca3af", fontSize: "13px", maxWidth: "420px", margin: "0 auto 16px" }}>
                                        We couldn't find this app. Try searching again.
                                    </p>
                                    <button onClick={handleBackToSearch} className="ugc-btn-outline">Search Again</button>
                                </div>
                            )}

                            {status === UGC_STATUS.FORBIDDEN && (
                                <div className="text-center" style={{ background: "rgba(246,37,90,0.05)", border: "1px solid rgba(246,37,90,0.25)", borderRadius: "16px", padding: "48px 20px" }}>
                                    <div style={{ width: "56px", height: "56px", background: "rgba(246,37,90,0.12)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                        <Lock size={24} color="#F6255A" />
                                    </div>
                                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>Unlock UGC Explorer</h6>
                                    <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13.5px", maxWidth: "440px", margin: "0 auto 12px", lineHeight: "1.5" }}>
                                        {upgradeInfo?.error || "Verified UGC video intelligence is available on higher-tier plans."}
                                    </p>
                                    {Array.isArray(upgradeInfo?.required_tiers) && upgradeInfo.required_tiers.length > 0 && (
                                        <div className="d-flex justify-content-center gap-2 flex-wrap mb-3">
                                            {upgradeInfo.required_tiers.map((tier, i) => (
                                                <span key={i} style={{ background: "rgba(246,37,90,0.15)", color: "#F6255A", fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "6px", textTransform: "uppercase" }}>
                                                    {tier}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <button onClick={() => navigate("/profile/plans")} className="ugc-btn-primary" style={{ margin: "0 auto" }}>
                                        Upgrade Plan
                                    </button>
                                </div>
                            )}
                            </>
                          )}
                        </div>
                    )}

                    <KeywordSuggestionsDetailsModal
                        show={showSearchModal}
                        handleClose={() => setShowSearchModal(false)}
                        title="Search Apps"
                        searchTerm={searchTerm}
                        onSearchChange={e => setSearchTerm(e.target.value)}
                        onSearchClick={() => { }}
                        onSelectApp={handleSelectApp}
                        selectButtonLabel="Explore UGC"
                    />
                </div>
            </div>

            <style>{`
                .ugc-video-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 18px;
                }
                /* Reels and TikTok videos are both vertical (9:16) source
                   video (VideoCard renders a portrait thumbnail for them) -
                   narrower columns keep the tiles proportioned like each
                   platform's own grid instead of stretching a portrait
                   thumbnail across a YouTube-width card. */
                .ugc-video-grid--reels {
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                }
                .ugc-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .ugc-clamp-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .ugc-search-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .ugc-search-actions {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .ugc-search-actions .ugc-btn-primary { width: 120px; }
                .ugc-find-app-card { padding: 30px; }
                /* min-width: 0 lets the box shrink below the input's intrinsic width on narrow screens */
                .ugc-search-box { flex: 1 1 260px; min-width: 0; }
                @media (max-width: 575.98px) {
                    .ugc-find-app-card { padding: 20px 16px; }
                    .ugc-search-row { align-items: stretch; }
                    .ugc-search-box { flex-basis: 100%; max-width: 100% !important; }
                    .ugc-search-actions { width: 100%; }
                    .ugc-search-actions .ugc-btn-primary,
                    .ugc-search-actions .ugc-btn-ghost {
                        flex: 1 1 0;
                        width: auto;
                        justify-content: center;
                    }
                }
                .ugc-btn-primary {
                    padding: 8px 16px;
                    border-radius: 10px;
                    background: #F6255A;
                    color: #fff;
                    border: none;
                    font-weight: 600;
                    cursor: pointer;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-size: 15px;
                    font-family: Rubik, sans-serif;
                    white-space: nowrap;
                    transition: background 0.15s ease;
                }
                .ugc-btn-primary:hover { background: #e11d4c; }
                .ugc-btn-outline {
                    background: rgba(246,37,90,0.1);
                    border: 1px solid #F6255A;
                    color: #F6255A;
                    padding: 12px 32px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    transition: background 0.15s ease;
                }
                .ugc-btn-outline:hover { background: rgba(246,37,90,0.18); }
                .ugc-btn-ghost {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #e8e8e8;
                    padding: 8px 14px;
                    border-radius: 10px;
                    font-weight: 600;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    white-space: nowrap;
                }
                .ugc-btn-ghost:hover:not(:disabled) { background: rgba(255,255,255,0.1); }
                .ugc-btn-ghost:disabled { opacity: 0.45; cursor: not-allowed; }
                .ugc-select-trigger {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #e8e8e8;
                    padding: 8px 12px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background 0.15s ease;
                }
                .ugc-select-trigger:hover { background: rgba(255,255,255,0.1); }
                .ugc-select-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    right: 0;
                    min-width: 170px;
                    background: #21222d;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.45);
                    padding: 6px;
                    z-index: 20;
                }
                .ugc-select-option {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 9px 10px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    color: #e8e8e8;
                    cursor: pointer;
                }
                .ugc-select-option:hover { background: rgba(255,255,255,0.06); }
                .ugc-select-option.is-active { color: #fff; background: rgba(246,37,90,0.1); }

                /* ── Comment sentiment ─────────────────────────────── */
                .ugc-sent-cta {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                    background: linear-gradient(135deg, rgba(246,37,90,0.09) 0%, rgba(246,37,90,0.02) 60%, rgba(255,255,255,0.01) 100%);
                    border: 1px solid rgba(246,37,90,0.22);
                    border-radius: 14px;
                    padding: 16px 20px;
                }
                .ugc-sent-cta-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: rgba(246,37,90,0.12);
                    border: 1px solid rgba(246,37,90,0.25);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                @media (max-width: 575.98px) {
                    .ugc-sent-cta-actions { width: 100%; }
                    .ugc-sent-cta-actions .ugc-btn-primary,
                    .ugc-sent-cta-actions .ugc-btn-ghost { flex: 1 1 auto; justify-content: center; }
                }
                .ugc-sent-tooltip {
                    position: absolute;
                    bottom: calc(100% + 8px);
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #2a2b38;
                    border: 1px solid rgba(255,255,255,0.1);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.45);
                    border-radius: 8px;
                    padding: 6px 10px;
                    font-size: 12px;
                    color: #fff;
                    white-space: nowrap;
                    z-index: 30;
                    pointer-events: none;
                }
                .ugc-sent-overview-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
                    gap: 16px;
                }
                .ugc-sent-platform-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 14px;
                }
                .ugc-sent-platform-card {
                    background: #1c1d27;
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 14px;
                    padding: 16px 18px;
                    transition: border-color 0.15s ease, background 0.15s ease;
                    outline: none;
                }
                .ugc-sent-platform-card.is-clickable { cursor: pointer; }
                .ugc-sent-platform-card.is-clickable:hover,
                .ugc-sent-platform-card.is-clickable:focus-visible { border-color: rgba(255,255,255,0.16); }
                .ugc-sent-platform-card.is-selected {
                    border-color: rgba(246,37,90,0.55);
                    background: linear-gradient(180deg, rgba(246,37,90,0.06), #1c1d27 70%);
                }
                .ugc-sent-detail-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
                    gap: 28px;
                }
                .ugc-sent-insight-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 14px;
                }
                .ugc-sent-insight-list {
                    margin: 0;
                    padding-left: 18px;
                    font-size: 13.5px;
                    line-height: 1.55;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .ugc-sent-insight-list li::marker { color: #565866; }
                .ugc-sent-quote-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 14px;
                }
                .ugc-sent-quote {
                    display: flex;
                    flex-direction: column;
                    background: #1c1d27;
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 12px;
                    padding: 16px;
                }
                .ugc-sent-comment {
                    display: flex;
                    gap: 12px;
                    background: rgba(255,255,255,0.02);
                    border: 1px solid rgba(255,255,255,0.05);
                    border-radius: 10px;
                    padding: 12px 14px;
                }
                .ugc-sent-filter {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #9ca3af;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    transition: background 0.15s ease;
                }
                .ugc-sent-filter:hover { background: rgba(255,255,255,0.07); }
                .ugc-sent-toggle {
                    display: inline-flex;
                    flex-wrap: wrap;
                    gap: 4px;
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 10px;
                    padding: 3px;
                }
                .ugc-sent-toggle button {
                    background: transparent;
                    border: none;
                    color: #9ca3af;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    padding: 6px 12px;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background 0.15s ease, color 0.15s ease;
                }
                .ugc-sent-toggle button:hover { color: #e8e8e8; }
                .ugc-sent-toggle button.is-active { background: rgba(246,37,90,0.14); color: #fff; }
                .ugc-sent-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: none;
                    border: none;
                    padding: 0;
                    color: #F6255A;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                }
                .ugc-sent-videoref {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    max-width: 260px;
                    color: #8b9bd1;
                    font-size: 12px;
                    text-decoration: none;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .ugc-sent-videoref:hover { color: #b4c0ea; text-decoration: underline; }
                .ugc-sent-video-row {
                    display: block;
                    padding: 10px 0;
                    text-decoration: none;
                }
                .ugc-sent-video-row:hover .ugc-clamp-2 { color: #fff !important; }
                @keyframes ugcSentPulse {
                    0%, 100% { opacity: 0.45; }
                    50% { opacity: 1; }
                }
                @media (max-width: 991.98px) {
                    .ugc-sent-overview-grid,
                    .ugc-sent-detail-grid { grid-template-columns: 1fr; }
                }
                @media (max-width: 575.98px) {
                    .ugc-sent-insight-grid { grid-template-columns: 1fr; }
                }
            `}</style>
        </React.Fragment>
    )
}

export default UGCExplorer
